/**
 * RTWO Event Bus — Federation Composite Engine Source Adapter
 *
 * Aggregates per-engine federation channels for the central Event Bus layer.
 * No events fabricated — relays and registrations remain unavailable until real activity.
 */

import type { EventId, RTWOEventType, TopicId } from '../domain-models';
import type {
  EnginePublishResult,
  EnginePublisherResult,
  EngineSourceAdapterStatus,
  EngineSubscriberResult,
  IEngineSourceAdapter,
} from './engine-source-adapter-interface';
import type { OperationalFederatedEngineSourceAdapter } from './operational-federated-engine-source-adapter';

export class FederationCompositeEngineSourceAdapter implements IEngineSourceAdapter {
  readonly adapterId = 'federation-composite-engine-source-adapter';
  readonly adapterName = 'Event Bus Federation Composite Adapter';

  constructor(private readonly getChannels: () => OperationalFederatedEngineSourceAdapter[]) {}

  private getReadyChannels(): OperationalFederatedEngineSourceAdapter[] {
    return this.getChannels().filter((channel) => channel.isReady());
  }

  async connect(): Promise<void> {
    for (const channel of this.getChannels()) {
      if (!channel.isReady()) {
        await channel.connect();
      }
    }
  }

  async disconnect(): Promise<void> {
    for (const channel of this.getChannels()) {
      await channel.disconnect();
    }
  }

  getStatus(): EngineSourceAdapterStatus {
    const ready = this.getReadyChannels();
    return ready.length > 0 ? 'connected' : 'not_configured';
  }

  isReady(): boolean {
    return this.getReadyChannels().length > 0;
  }

  async publishEvent(
    eventType: RTWOEventType,
    sourceModule: string,
    targetModule: string | null,
    payloadReference: string | null
  ): Promise<EnginePublishResult | null> {
    const channel = this.getChannels().find((entry) => entry.sourceModuleId === sourceModule);
    return channel?.publishEvent(eventType, sourceModule, targetModule, payloadReference) ?? null;
  }

  async registerPublisher(
    moduleId: string,
    supportedEvents: RTWOEventType[]
  ): Promise<EnginePublisherResult | null> {
    const channel = this.getChannels().find((entry) => entry.sourceModuleId === moduleId);
    return channel?.registerPublisher(moduleId, supportedEvents) ?? null;
  }

  async registerSubscriber(
    moduleId: string,
    subscribedTopics: TopicId[]
  ): Promise<EngineSubscriberResult | null> {
    const channel = this.getChannels().find((entry) => entry.sourceModuleId === moduleId);
    return channel?.registerSubscriber(moduleId, subscribedTopics) ?? null;
  }

  async routeEvent(eventId: EventId): Promise<boolean> {
    for (const channel of this.getReadyChannels()) {
      if (await channel.routeEvent(eventId)) {
        return true;
      }
    }
    return false;
  }

  async deliverEvent(eventId: EventId, subscriberId: string): Promise<boolean> {
    for (const channel of this.getReadyChannels()) {
      if (await channel.deliverEvent(eventId, subscriberId)) {
        return true;
      }
    }
    return false;
  }
}
